import { useEffect, useState, useCallback, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useStore } from './lib/store';
import { api } from './lib/api';
import { transformToGraphData, getNodeLabel } from './lib/graph-utils';
import type { GraphData, GraphNode, EntityType } from './types';

import LoginScreen from './components/LoginScreen';
import LoadingScreen from './components/LoadingScreen';
import GraphView from './components/GraphView';
import ListView from './components/ListView';
import NodeCard from './components/NodeCard';
import ActionButtons from './components/ActionButtons';
import CreateModal from './components/CreateModal';
import AIChat from './components/AIChat';

function getUpdatedLabel(entityType: EntityType, data: any): string {
  return getNodeLabel(entityType, data);
}

type ViewMode = '3d' | '2d' | 'list' | 'chat';

const VIEW_LABELS: Record<ViewMode, string> = {
  '3d': '3D',
  '2d': '2D',
  'list': 'List',
  'chat': 'AI',
};

function App() {
  const {
    isAuthenticated,
    isLoading,
    setLoading,
    setAuthenticated,
    setUser,
    setToken,
    user,
  } = useStore();

  const [error, setError] = useState('');
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createModalType, setCreateModalType] = useState<'person' | 'project' | 'task' | 'ai_note' | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('3d');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showCard, setShowCard] = useState(true);
  const [brainMode, setBrainMode] = useState(false);

  // Compute highlighted nodes from search
  const highlightedNodes = useMemo(() => {
    const set = new Set<string>();
    if (!searchQuery.trim()) return set;
    const q = searchQuery.toLowerCase();
    for (const node of graphData.nodes) {
      const label = node.label.toLowerCase();
      const desc = (node.description || '').toLowerCase();
      if (label.includes(q) || desc.includes(q)) {
        set.add(node.id);
      }
    }
    return set;
  }, [graphData.nodes, searchQuery]);

  // Load graph data
  const loadGraph = useCallback(async () => {
    try {
      const [notesRes, aiNotesRes, peopleRes, projectsRes, tasksRes, edgesRes,
             moodsRes, bodiesRes, foodsRes, activitiesRes, resourcesRes, reflectionsRes] = await Promise.all([
        api.getNotes(),
        api.getAINotes(),
        api.getPeople(),
        api.getProjects(),
        api.getTasks(),
        api.getEdges(),
        api.getMoods(),
        api.getBodies(),
        api.getFoods(),
        api.getActivities(),
        api.getResources(),
        api.getReflections(),
      ]);

      const data = transformToGraphData(
        notesRes.notes || [],
        aiNotesRes.ai_notes || [],
        peopleRes.people || [],
        projectsRes.projects || [],
        tasksRes.tasks || [],
        edgesRes.edges || [],
        moodsRes.moods || [],
        bodiesRes.bodies || [],
        foodsRes.foods || [],
        activitiesRes.activities || [],
        resourcesRes.resources || [],
        reflectionsRes.reflections || [],
      );
      setGraphData(data);
    } catch (err) {
      console.error('Failed to load graph:', err);
      setError('Failed to load graph data');
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      let token = urlToken;

      if (!token) {
        token = localStorage.getItem('neron_token');
      }

      if (token) {
        try {
          const result = await api.validateToken(token);
          if (result.valid && result.user) {
            api.setToken(token);
            setToken(token);
            setUser(result.user);
            setAuthenticated(true);
            if (urlToken) {
              window.history.replaceState({}, '', window.location.pathname);
            }
          } else {
            api.clearToken();
            setError(result.error || 'Session expired');
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          api.clearToken();
          setError('Session expired');
        }
      }

      setLoading(false);
    };

    initAuth();
  }, [setAuthenticated, setLoading, setToken, setUser]);

  // Load graph when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadGraph();
    }
  }, [isAuthenticated, loadGraph]);

  const handleLogin = async (token: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await api.validateToken(token);
      if (result.valid && result.user) {
        api.setToken(token);
        setToken(token);
        setUser(result.user);
        setAuthenticated(true);
      } else {
        setError(result.error || 'Invalid token');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed');
    }
    setLoading(false);
  };

  const handleLogout = useCallback(() => {
    api.clearToken();
    setToken(null);
    setUser(null);
    setAuthenticated(false);
    window.location.reload();
  }, [setAuthenticated, setToken, setUser]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await loadGraph();
    setIsRefreshing(false);
  }, [loadGraph]);

  const handleNodeClick = useCallback((node: GraphNode) => {
    setSelectedNode(node);
    setShowCard(true);
  }, []);

  const handleShowOnGraph = useCallback((mode: '3d' | '2d') => {
    setViewMode(mode);
    setShowCard(false);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleTaskStatusChange = useCallback((nodeId: string, newStatus: string) => {
    const oldNode = graphData.nodes.find(n => n.id === nodeId);
    const oldStatus = oldNode ? (oldNode.data as any).status : 'pending';

    const applyStatus = (s: string) => {
      setGraphData(prev => ({
        ...prev,
        nodes: prev.nodes.map(n =>
          n.id === nodeId ? { ...n, data: { ...(n.data as any), status: s } } : n
        ),
      }));
      setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...(prev.data as any), status: s } } : prev);
    };

    applyStatus(newStatus);
    const id = parseInt(nodeId.split(':')[1]);
    api.updateTask(id, { status: newStatus } as any).catch(() => applyStatus(oldStatus));
  }, [graphData.nodes]);

  const handleTaskPriorityChange = useCallback((nodeId: string, newPriority: number) => {
    const oldNode = graphData.nodes.find(n => n.id === nodeId);
    const oldPriority = oldNode ? (oldNode.data as any).priority : 5;

    const applyPriority = (p: number) => {
      setGraphData(prev => ({
        ...prev,
        nodes: prev.nodes.map(n =>
          n.id === nodeId ? { ...n, data: { ...(n.data as any), priority: p } } : n
        ),
      }));
      setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...(prev.data as any), priority: p } } : prev);
    };

    applyPriority(newPriority);
    const id = parseInt(nodeId.split(':')[1]);
    api.updateTask(id, { priority: newPriority } as any).catch(() => applyPriority(oldPriority));
  }, [graphData.nodes]);

  const handleEntityUpdate = useCallback(async (nodeId: string, entityType: string, updates: Record<string, any>) => {
    const id = parseInt(nodeId.split(':')[1]);
    const oldNode = graphData.nodes.find(n => n.id === nodeId);
    if (!oldNode) return;
    const oldData = { ...oldNode.data as any };

    // Optimistic update
    const applyUpdate = (data: Record<string, any>) => {
      setGraphData(prev => ({
        ...prev,
        nodes: prev.nodes.map(n =>
          n.id === nodeId ? { ...n, data: { ...(n.data as any), ...data }, label: getUpdatedLabel(n.entityType, { ...(n.data as any), ...data }) } : n
        ),
      }));
      setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...(prev.data as any), ...data }, label: getUpdatedLabel(prev.entityType, { ...(prev.data as any), ...data }) } : prev);
    };

    applyUpdate(updates);

    try {
      switch (entityType) {
        case 'note': await api.updateNote(id, updates); break;
        case 'person': await api.updatePerson(id, updates); break;
        case 'project': await api.updateProject(id, updates); break;
        case 'task': await api.updateTask(id, updates as any); break;
        case 'mood': await api.updateMood(id, updates); break;
        case 'body': await api.updateBody(id, updates); break;
        case 'food': await api.updateFood(id, updates); break;
        case 'activity': await api.updateActivity(id, updates); break;
        case 'resource': await api.updateResource(id, updates); break;
        case 'reflection': await api.updateReflection(id, updates); break;
      }
    } catch {
      applyUpdate(oldData);
    }
  }, [graphData.nodes]);

  const handleEdgeCreate = useCallback(async (edge: { from_type: string; from_id: number; to_type: string; to_id: number; relationship: string }) => {
    try {
      await api.createEdge(edge);
      await loadGraph();
    } catch (err) {
      console.error('Failed to create edge:', err);
    }
  }, [loadGraph]);

  const handleEdgeDelete = useCallback(async (edgeId: number) => {
    try {
      await api.deleteEdge(edgeId);
      await loadGraph();
    } catch (err) {
      console.error('Failed to delete edge:', err);
    }
  }, [loadGraph]);

  const handleEntityDelete = useCallback(async (nodeId: string, entityType: string) => {
    const id = parseInt(nodeId.split(':')[1]);
    await api.deleteEntity(entityType, id);
    setSelectedNode(null);
    setGraphData(prev => ({
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== nodeId),
      links: prev.links.filter(l => {
        const src = typeof l.source === 'string' ? l.source : (l.source as any)?.id;
        const tgt = typeof l.target === 'string' ? l.target : (l.target as any)?.id;
        return src !== nodeId && tgt !== nodeId;
      }),
    }));
  }, []);

  // Auto-disable brain mode when switching away from 3D
  useEffect(() => {
    if (viewMode !== '3d') setBrainMode(false);
  }, [viewMode]);

  const toggleSearch = useCallback(() => {
    setSearchOpen(prev => {
      if (prev) setSearchQuery('');
      return !prev;
    });
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  const showSearch = viewMode !== 'chat';

  return (
    <div className="h-screen flex flex-col bg-black text-white overflow-hidden">
      {/* Header */}
      <header className="bg-black border-b border-gray-900 px-4 py-2 shrink-0">
        <div className="flex items-center justify-between">
          {/* Left: logo */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center border border-white/10">
              <span className="text-xs font-bold text-white">N</span>
            </div>
          </div>

          {/* Center: view mode toggle */}
          <div className="flex bg-gray-900/80 rounded-lg border border-gray-800 overflow-hidden">
            {(['3d', '2d', 'list', 'chat'] as ViewMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === mode
                    ? 'text-white bg-gray-700'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {VIEW_LABELS[mode]}
              </button>
            ))}
          </div>

          {/* Right: search + user + logout */}
          <div className="flex items-center gap-2 shrink-0">
            {showSearch && (
              <button
                onClick={toggleSearch}
                className={`p-1.5 rounded-lg transition-colors ${
                  searchOpen ? 'text-white bg-gray-800' : 'text-gray-500 hover:text-white'
                }`}
              >
                {searchOpen ? <X size={16} /> : <Search size={16} />}
              </button>
            )}
            <span className="text-xs text-gray-600 hidden sm:inline">
              {user?.firstName || user?.username}
            </span>
            <button
              onClick={handleLogout}
              className="text-xs text-gray-600 hover:text-white transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Search bar — slides open */}
        {showSearch && searchOpen && (
          <div className="mt-2 pb-1">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entities..."
                autoFocus
                className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-700"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-red-900/20 border-b border-red-900/50 text-red-400 text-sm shrink-0">
          {error}
          <button
            onClick={() => setError('')}
            className="ml-2 text-gray-500 hover:text-white"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative bg-black">
        {viewMode === 'chat' ? (
          <AIChat />
        ) : viewMode === 'list' ? (
          <ListView
            data={graphData}
            onNodeClick={handleNodeClick}
            searchQuery={searchQuery}
            onTaskStatusChange={handleTaskStatusChange}
          />
        ) : (
          <GraphView
            data={graphData}
            selectedNode={selectedNode}
            highlightedNodes={highlightedNodes}
            onNodeClick={handleNodeClick}
            onBackgroundClick={handleBackgroundClick}
            is3D={viewMode === '3d'}
            brainMode={brainMode}
            onBrainModeToggle={setBrainMode}
          />
        )}

        {/* Node detail card — hidden during brain mode */}
        {selectedNode && showCard && !brainMode && (
          <NodeCard
            node={selectedNode}
            links={graphData.links}
            nodes={graphData.nodes}
            onClose={() => setSelectedNode(null)}
            onNodeSelect={handleNodeClick}
            onDelete={() => handleEntityDelete(selectedNode.id, selectedNode.entityType)}
            onShowOnGraph={handleShowOnGraph}
            onTaskStatusChange={selectedNode.entityType === 'task' ? (status: string) => handleTaskStatusChange(selectedNode.id, status) : undefined}
            onTaskPriorityChange={selectedNode.entityType === 'task' ? (priority: number) => handleTaskPriorityChange(selectedNode.id, priority) : undefined}
            onEntityUpdate={(updates) => handleEntityUpdate(selectedNode.id, selectedNode.entityType, updates)}
            onEdgeCreate={handleEdgeCreate}
            onEdgeDelete={handleEdgeDelete}
          />
        )}

        {/* Action buttons (visible in non-chat modes when no node selected, hidden during brain mode) */}
        {viewMode !== 'chat' && !brainMode && !(selectedNode && showCard) && (
          <ActionButtons
            onCreatePerson={() => setCreateModalType('person')}
            onCreateProject={() => setCreateModalType('project')}
            onCreateTask={() => setCreateModalType('task')}
            onRefresh={handleRefresh}
            isLoading={isRefreshing}
          />
        )}

        {/* Create modals */}
        {createModalType && (
          <CreateModal
            isOpen={true}
            type={createModalType}
            onClose={() => setCreateModalType(null)}
            onSuccess={handleRefresh}
          />
        )}
      </main>
    </div>
  );
}

export default App;
